'use client'

import {ChevronDown} from 'lucide-react'
import {useIntl} from 'react-intl'
import {Checkbox} from '@/components/ui/checkbox'
import {Separator} from '@/components/ui/separator'

export type ProductFacetOption = {
  value: string
  label: string
}

export type ProductFacetDefinition<TFilterKey extends string = string> = {
  key: TFilterKey
  label: string
  options: ProductFacetOption[]
  values: string[]
  counts: Map<string, number>
}

const facetCheckboxId = (key: string, value: string) =>
  `facet-${key}-${value.toLowerCase().replace(/[^a-z0-9_-]+/g, '-')}`

type ProductFacetSidebarProps<TFilterKey extends string = string> = {
  definitions: ProductFacetDefinition<TFilterKey>[]
  collapsedSections: Record<string, boolean>
  onToggleSection: (key: TFilterKey) => void
  onToggleOption: (key: TFilterKey, value: string) => void
  onClearAll: () => void
}

export function ProductFacetSidebar<TFilterKey extends string = string>({
  definitions,
  collapsedSections,
  onToggleSection,
  onToggleOption,
  onClearAll,
}: ProductFacetSidebarProps<TFilterKey>) {
  const intl = useIntl()

  return (
    <aside className="lg:sticky lg:top-20">
      <div className="flex items-center justify-between">
        <h2 className="u-body-sm font-semibold text-foreground">
          {intl.formatMessage({id: 'catalog.filtering', defaultMessage: 'Filtering'})}
        </h2>
        <button
          type="button"
          onClick={onClearAll}
          className="cursor-pointer u-body-sm text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
        >
          {intl.formatMessage({id: 'catalog.clearFilters', defaultMessage: 'Clear filters'})}
        </button>
      </div>

      <Separator className="my-site-sm" />

      <div className="space-y-site-sm">
        {definitions.map((definition) => {
          const panelId = `facet-panel-${definition.key}`
          return (
            <div key={definition.key}>
              <button
                type="button"
                onClick={() => onToggleSection(definition.key)}
                className="flex w-full items-center justify-between u-body-sm font-semibold text-foreground"
                aria-expanded={!collapsedSections[definition.key]}
                aria-controls={panelId}
              >
                {definition.label}
                <ChevronDown
                  className={`size-4 shrink-0 transition-transform duration-200 ${
                    collapsedSections[definition.key] ? '' : 'rotate-180'
                  }`}
                />
              </button>
              {!collapsedSections[definition.key] ? (
                <div id={panelId} className="mt-3 space-y-2.5">
                  {definition.options
                    .filter((option) => {
                      const count = definition.counts.get(option.value) || 0
                      const selected = definition.values.includes(option.value)
                      return selected || count > 0
                    })
                    .map((option) => {
                      const checked = definition.values.includes(option.value)
                      const count = definition.counts.get(option.value) || 0
                      const checkboxId = facetCheckboxId(definition.key, option.value)
                      return (
                        <label
                          key={option.value}
                          htmlFor={checkboxId}
                          className="flex cursor-pointer items-center gap-2.5 u-body-sm"
                        >
                          <Checkbox
                            id={checkboxId}
                            aria-label={option.label}
                            checked={checked}
                            disabled={!checked && count === 0}
                            onCheckedChange={() => onToggleOption(definition.key, option.value)}
                          />
                          <span className="flex-1 text-foreground">{option.label}</span>
                          <span className="text-xs text-muted-foreground">({count})</span>
                        </label>
                      )
                    })}
                </div>
              ) : null}
              <Separator className="mt-site-sm" />
            </div>
          )
        })}
      </div>
    </aside>
  )
}
